const PENDING_NOTIFICATION_NAV_KEY = 'agentchat:pending-notification-nav'
const PENDING_NOTIFICATION_NAV_TTL_MS = 45_000
const PENDING_NOTIFICATION_NAV_CLEAR_DELAY_MS = 8_000

type PendingNotificationNav = {
  url: string
  expires_at: number
}

function browserStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    return window.sessionStorage
  } catch {
    return null
  }
}

function parsePending(raw: string | null): PendingNotificationNav | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<PendingNotificationNav>
    if (typeof parsed.url !== 'string' || !parsed.url.trim()) return null
    if (typeof parsed.expires_at !== 'number' || parsed.expires_at <= Date.now()) return null
    return { url: parsed.url, expires_at: parsed.expires_at }
  } catch {
    return null
  }
}

export function rememberPendingNotificationNav(url: string): void {
  const storage = browserStorage()
  if (!storage || !url.trim()) return
  const pending: PendingNotificationNav = {
    url,
    expires_at: Date.now() + PENDING_NOTIFICATION_NAV_TTL_MS,
  }
  try {
    storage.setItem(PENDING_NOTIFICATION_NAV_KEY, JSON.stringify(pending))
  } catch {
    // Navigation still continues; we just lose reload recovery.
  }
}

export function getPendingNotificationNav(): string | null {
  const storage = browserStorage()
  if (!storage) return null
  const pending = parsePending(storage.getItem(PENDING_NOTIFICATION_NAV_KEY))
  if (!pending) {
    try { storage.removeItem(PENDING_NOTIFICATION_NAV_KEY) } catch { /* ignore */ }
    return null
  }
  return pending.url
}

export function applyPendingNotificationNavBeforeReactMount(): void {
  const pendingUrl = getPendingNotificationNav()
  if (!pendingUrl) return
  try {
    const target = new URL(pendingUrl, window.location.origin)
    if (target.origin !== window.location.origin) return
    const targetPath = `${target.pathname}${target.search}${target.hash}`
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (targetPath === currentPath) return
    window.history.replaceState(window.history.state, '', targetPath)
  } catch {
    // App.tsx still drains the pending notification after mount.
  }
}

export function clearPendingNotificationNav(url?: string): void {
  const storage = browserStorage()
  if (!storage) return
  if (url) {
    const pending = parsePending(storage.getItem(PENDING_NOTIFICATION_NAV_KEY))
    if (pending && pending.url !== url) return
  }
  try { storage.removeItem(PENDING_NOTIFICATION_NAV_KEY) } catch { /* ignore */ }
}

export function clearPendingNotificationNavSoon(url: string): () => void {
  const timer = window.setTimeout(() => {
    clearPendingNotificationNav(url)
  }, PENDING_NOTIFICATION_NAV_CLEAR_DELAY_MS)
  return () => window.clearTimeout(timer)
}
